import { useState } from "react";
import { createPortal } from "react-dom";
import Api from "../../services/Api";
import { useResponsive } from "../../hooks/useResponsive";

const emptyForm = {
  nome: "",
  contato: "",
  descricao: "",
  nome_falecido: "",
  data_nascimento: "",
  data_falecimento: ""
};

function initialForm(falecido) {
  return {
    ...emptyForm,
    nome_falecido: falecido?.nome_completo || "",
    data_nascimento: falecido?.data_nascimento || "",
    data_falecimento: falecido?.data_falecimento || "",
  };
}

export default function CorrecaoModal({ isOpen, onClose, falecido }) {
  const { isMobile } = useResponsive();
  const [form, setForm] = useState(() => initialForm(falecido));
  const [status, setStatus] = useState(null); // "sending" | "success" | "error"

  if (!isOpen) return null;

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handlePhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    let masked = digits;
    if (digits.length > 2) masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length > 7) masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    handleChange("contato", masked);
  };

  const phoneValid = /^\(\d{2}\) \d{5}-\d{4}$/.test(form.contato);

  const handleSubmit = () => {
    if (!form.nome.trim() || !form.descricao.trim()) {
      alert("Preencha todos os campos obrigatórios antes de enviar.");
      return;
    }
    if (!phoneValid) {
      alert("Informe o telefone no formato (XX) XXXXX-XXXX.");
      return;
    }
    setStatus("sending");

    const fd = new FormData();
    fd.append("falecido_id", falecido.id);
    fd.append("falecido_nome", falecido.nome_completo);
    fd.append("nome_solicitante", form.nome);
    fd.append("contato", form.contato);
    fd.append("descricao", form.descricao);

    Api.solicitarCorrecao(fd)
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"));
  };

  const handleClose = () => {
    setForm(initialForm(falecido));
    setStatus(null);
    onClose();
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #ddd",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: "#E8956A",
    marginBottom: 4,
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          width: isMobile ? "min(95vw, 480px)" : 480,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              color: "#fff",
            }}
          >
            Solicitar correção
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              borderRadius: "50%",
              width: 30,
              height: 30,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {status === "success" ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#4caf50", fontWeight: 600, marginBottom: 8 }}>
                Solicitação enviada com sucesso!
              </p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888" }}>
                A administração analisará e fará a correção.
              </p>
            </div>
          ) : status === "error" ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#e53935", fontWeight: 600, marginBottom: 8 }}>
                Erro ao enviar solicitação.
              </p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888" }}>
                Tente novamente ou entre em contato diretamente com a administração.
              </p>
            </div>
          ) : (
            <>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", marginBottom: 16, marginTop: 0 }}>
                Informe os dados corrigidos para{" "}
                <strong style={{ color: "#555" }}>{falecido?.nome_completo}</strong>.
                A administração analisará e fará a correção.
              </p>

              {/* Dados do solicitante */}
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#999", marginBottom: 10, marginTop: 0, textTransform: "uppercase", letterSpacing: 1 }}>
                Seus dados
              </p>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Seu nome *</label>
                <input
                  type="text"
                  value={form.nome}
                  placeholder="Nome completo"
                  onChange={(e) => handleChange("nome", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Telefone para contato *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.contato}
                  placeholder="(XX) XXXXX-XXXX"
                  onChange={(e) => handlePhone(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: form.contato && !phoneValid ? "#e53935" : "#ddd",
                  }}
                />
                {form.contato && !phoneValid && (
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#e53935", display: "block", marginTop: 4 }}>
                    Formato inválido. Use (XX) XXXXX-XXXX.
                  </span>
                )}
              </div>

              {/* Dados sugeridos */}
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#999", marginBottom: 10, marginTop: 6, textTransform: "uppercase", letterSpacing: 1 }}>
                Dados corretos do falecido
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Nome do falecido</label>
                <input
                  type="text"
                  value={form.nome_falecido}
                  placeholder="Nome completo correto"
                  onChange={(e) => handleChange("nome_falecido", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 4 }}>
                <label style={labelStyle}>Descrição da correção *</label>
                <textarea
                  value={form.descricao}
                  placeholder="Descreva o que está incorreto e qual seria a informação correta..."
                  maxLength={600}
                  onChange={(e) => handleChange("descricao", e.target.value)}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 90 }}
                />
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#bbb", display: "block", textAlign: "right" }}>
                  {form.descricao.length}/600
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "12px 24px 20px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid #ddd",
              background: "#f5f5f5",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              cursor: "pointer",
              color: "#555",
            }}
          >
            {status === "success" || status === "error" ? "Fechar" : "Cancelar"}
          </button>
          {!status && (
            <button
              onClick={handleSubmit}
              disabled={status === "sending"}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                color: "#fff",
                opacity: status === "sending" ? 0.7 : 1,
              }}
            >
              {status === "sending" ? "Enviando..." : "Enviar solicitação"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
